import * as React from "react";
import {
  Modal,
  Card,
  Input,
  Form,
  Button,
  Upload,
  message,
  Typography,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import {
  getSamplePhonebook,
  createPhonebook,
} from "../../../services/adminController";
import { useQuery, useMutation } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import { decryptData } from "../../../utils";

const { Text } = Typography;

type Props = {
  status: boolean;
  onCancel: () => void;
  refetchPhonebooks: () => void;
};

export const AddPhonebookModal: React.FC<Props> = ({
  status,
  onCancel,
  refetchPhonebooks,
}) => {
  const [form] = Form.useForm();
  const [loanIds, setLoanIds] = React.useState<string[]>([]);
  const [fileList, setFileList] = React.useState<any[]>([]);
  const { data, refetch, isFetching } = useQuery({
    queryKey: ["sampleExcel"],
    queryFn: getSamplePhonebook,
    enabled: false,
  });

  React.useEffect(() => {
    if (status) {
      form.resetFields();
      setLoanIds([]);
      setFileList([]);
    }
  }, [status]);

  const { mutate } = useMutation({
    mutationFn: createPhonebook,
    onSuccess: async () => {
      message.success("Phonebook created successfully!");
      form.resetFields();
      setLoanIds([]);
      setFileList([]);
      onCancel();
      await refetchPhonebooks();
    },
    onError: () => {
      message.error("Failed to create phonebook.");
    },
  });

  const onFinish = (values: any) => {
    if (loanIds.length === 0) {
      message.warning("No loan IDs found in uploaded file.");
      return;
    }
    const dataToSend = {
      phonebookname: values.name,
      loanIds,
    };

    mutate(dataToSend);
  };
  const handleExcel = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      const extractedLoanIds = jsonData
        .map((row: any) => row.loanId)
        .filter((id: any) => id !== undefined && id !== "");
      setLoanIds(extractedLoanIds);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDownloadSample = async () => {
    try {
      const res = await refetch();
      const fileUrl = decryptData(res.data?.data);
      if (fileUrl) {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = "Sample_excel_template.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        message.error("Failed to fetch download link.");
      }
    } catch (err) {
      message.error("Download failed.");
    }
  };

  // const beforeUpload = (file: File) => {
  //   const isExcel =
  //     file.type ===
  //       "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
  //     file.type === "application/vnd.ms-excel";
  //   if (!isExcel) {
  //     message.error("You can only upload Excel files!");
  //     return Upload.LIST_IGNORE;
  //   }
  //   handleExcel(file);
  //   return false;
  // };

  return (
    <Modal width={800} open={status} footer={null} onCancel={onCancel}>
      <Card title="Create Phonebook">
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="Phonebook Info"
            name="name"
            rules={[
              { required: true, message: "Please input your phonebook name!" },
            ]}
          >
            <Input
              placeholder="Enter Phonebook Name
"
            />
          </Form.Item>

          <Form.Item label="Upload Excel File">
            <Text type="secondary" className="text-xs text-gray-500">
              We recommend you to{" "}
              <Button
                size="small"
                type="link"
                onClick={handleDownloadSample}
                loading={isFetching}
              >
                Download sample Excel template
              </Button>
              and upload loan IDs in same format to avoid errors.
            </Text>
            <Upload
              beforeUpload={(file) => {
                handleExcel(file);
                setFileList([file]);
                return false;
              }}
              fileList={fileList}
              onRemove={() => setFileList([])}
              maxCount={1}
              accept=".xlsx,.xls"
            >
              <Button icon={<UploadOutlined />}>Browse Excel</Button>
            </Upload>
          </Form.Item>
          <Form.Item>
            <div className="flex justify-center space-x-4">
              <Button type="primary" htmlType="submit" className="w-[20%] mr-3">
                Submit
              </Button>
              <Button type="default" onClick={onCancel} className=" w-[20%]">
                Cancel
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
    </Modal>
  );
};
